
import {
  HostMessage,
  HostMessageType,
  generateHostMessage,
  generators,
  type HostScreenshotGrabCompletePayload,
  type PlaycastMessage,
  type PlaycastHostMessages,
} from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import { machineCredentialsState } from '../../../stores/shared';
import { realtimeMessageRouter } from '../../../../services/MessageRouter';
import { USE_MESSAGE_ROUTER } from '../../../../config/featureFlags';
import { ipcSend } from '../../../messaging/protocols/ipc';
import { registerMacroRecordingUploadVideoKey } from '../../../messaging/handlers/macroRecordingUploadVideoKey';
import { sendMessage as hostRealtimeSend } from '../index';

/**
 * Presigned URLs carry their signature in the query string. They are secrets for
 * the lifetime of the signature and MUST NOT be logged.
 */
export function redactPresignedUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '[invalid-url]';
  }
}

/**
 * Best-effort host extraction for telemetry. Strips the presigned query
 * string so we can surface the endpoint in Sentry without leaking the
 * signature on the URL.
 */
function safeUploadHost(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return 'unparseable_url';
  }
}

function logHostMessage(message: HostMessage): void {
  if ('uploadUrl' in message && typeof message.uploadUrl === 'string') {
    console.log('Host message:', {
      ...message,
      uploadUrl: redactPresignedUrl(message.uploadUrl),
    });
    return;
  }
  console.log('Host message:', message);
}

/**
 * Handle a `screenshotGrabComplete` IPC reply that originated in
 * host-electron's `mainSend(generators.host.screenshotGrabComplete(...))`.
 *
 * Called from `messaging/handlers/ipc.ts` — the central IPC router. A
 * previous incarnation of this listener registered itself directly with
 * `window.playcast.ipc.on('mainMessage', ...)`, but the preload's
 * mainMessage channel is a single-handler slot (last writer wins), and the
 * central router's registration happened later in startup and silently
 * overwrote it. Routing through the central handler eliminates that race.
 *
 * Forwards the result onward to the realtime API as
 * `HOST_SCREENSHOT_GRAB_COMPLETE`.
 */
export const handleScreenshotGrabCompleteIpcMessage = (
  message: PlaycastMessage<PlaycastHostMessages>,
): void => {
  // host-electron only sends shapes produced by
  // `generators.host.screenshotGrabComplete`, which constrains the body to
  // the discriminated union — narrow on `success` downstream.
  const result = message.body?.message as HostScreenshotGrabCompletePayload;
  // Sentry.logger.* (not captureMessage) is the unified-logs API — the
  // host-electron `sentryLog` bridge also funnels into this stream.
  Sentry.logger.info(
    `[Screenshot Grab] IPC reply received from host-electron (success=${result.success})`,
    {
      requestId: result.requestId,
      success: result.success,
      sizeBytes: result.success ? result.sizeBytes : undefined,
      error: result.success ? undefined : result.error,
    },
  );
  const wsMessage = generateHostMessage(HostMessageType.HOST_SCREENSHOT_GRAB_COMPLETE, result);
  hostRealtimeSend(wsMessage);
};

export const handleHostMessage = (message: HostMessage) => {
  logHostMessage(message);

  // Route through new coordinator architecture if enabled
  if (USE_MESSAGE_ROUTER) {
    console.log('[Host Messages] 🎯 Routing HOST message through coordinator architecture:', message.type);
    realtimeMessageRouter.route(message as any);
  }

  // Legacy handling (dual operation during strangler-fig migration)
  switch (message.type) {
    case HostMessageType.HOST_MACHINE_CREDENTIALS: {
      console.log('[Machine Credentials] Received credentials from realtime API');

      // Update state store with credentials (will be replicated to host-electron)
      machineCredentialsState.updateState({
        epic: message.epic,
        steam: message.steam,
      });

      console.log('[Machine Credentials] Credentials stored and replicated to host-electron');
      break;
    }

    case HostMessageType.HOST_SCREENSHOT_GRAB_COMMAND: {
      const { uploadUrl, requestId } = message;
      Sentry.logger.info('[Screenshot Grab] WS command received, forwarding to host-electron', {
        requestId,
        uploadHost: safeUploadHost(uploadUrl),
      });
      ipcSend(generators.host.screenshotGrab(uploadUrl, requestId));
      break;
    }

    case HostMessageType.HOST_SCHEDULED_SCREENSHOT_GRAB_COMMAND: {
      const { uploadUrl, requestId } = message;
      Sentry.logger.info('[Scheduled Screenshot Grab] WS command received, forwarding to host-electron', {
        requestId,
        uploadHost: safeUploadHost(uploadUrl),
      });
      ipcSend(generators.host.scheduledScreenshotGrab(uploadUrl, requestId));
      break;
    }

    case HostMessageType.HOST_MACRO_RECORDING_UPLOAD_COMMAND: {
      const { takeId, campaignId, uploadUrl, requestId } = message;
      registerMacroRecordingUploadVideoKey(requestId, `macro-recordings/${campaignId}/${takeId}.mp4`);
      Sentry.logger.info('[Macro Recording Upload] macro-recorder WS command received, forwarding to host-electron', {
        feature: 'macro-recorder', takeId,
        requestId,
        uploadHost: safeUploadHost(uploadUrl),
      });
      ipcSend(generators.host.macroRecordingUpload({ takeId, campaignId, uploadUrl, requestId }));
      break;
    }
  }
};

